import { useState } from "react";
import type { ActivityLevel, DietPattern } from "@/types";
import type { OnboardingDraft } from "./OnboardingFlow";

const ACTIVITY_OPTIONS: { value: ActivityLevel; label: string; hint: string }[] = [
  { value: "sedentary", label: "Sedentary", hint: "Mostly sitting" },
  { value: "light", label: "Light", hint: "Occasional walks" },
  { value: "moderate", label: "Moderate", hint: "Regular exercise" },
  { value: "active", label: "Active", hint: "Daily exercise" },
];

const DIET_OPTIONS: { value: DietPattern; label: string }[] = [
  { value: "omnivore", label: "Omnivore" },
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "pescatarian", label: "Pescatarian" },
];

export default function Step3Preferences({
  draft,
  onSubmit,
  onBack,
  submitting,
}: {
  draft: OnboardingDraft;
  onSubmit: (patch: Partial<OnboardingDraft>) => void;
  onBack: () => void;
  submitting: boolean;
}) {
  const [activityLevel, setActivityLevel] = useState<ActivityLevel | undefined>(
    draft.activityLevel
  );
  const [dietPattern, setDietPattern] = useState<DietPattern | undefined>(draft.dietPattern);
  const [allergyInput, setAllergyInput] = useState("");
  const [allergies, setAllergies] = useState<string[]>(draft.allergies ?? []);

  const canSubmit = activityLevel && dietPattern;

  function addAllergy() {
    const value = allergyInput.trim();
    if (value && !allergies.includes(value)) {
      setAllergies([...allergies, value]);
    }
    setAllergyInput("");
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold text-forest-700">A few preferences</h1>
        <p className="mt-1 text-sm text-forest-500">
          This shapes which foods and nutrients we pay closest attention to.
        </p>
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-ink">
          Typical activity level
        </span>
        <div className="grid grid-cols-2 gap-2">
          {ACTIVITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setActivityLevel(opt.value)}
              className={`rounded-card border px-3 py-2 text-left ${
                activityLevel === opt.value
                  ? "border-forest-700 bg-forest-50"
                  : "border-line"
              }`}
            >
              <p className="text-sm font-medium text-ink">{opt.label}</p>
              <p className="text-xs text-forest-500">{opt.hint}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-ink">Dietary pattern</span>
        <div className="grid grid-cols-2 gap-2">
          {DIET_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDietPattern(opt.value)}
              className={`chip ${dietPattern === opt.value ? "chip-selected" : ""}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="mb-1 block text-sm font-medium text-ink">
          Allergies or intolerances
        </span>
        <div className="flex gap-2">
          <input
            className="input"
            value={allergyInput}
            onChange={(e) => setAllergyInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAllergy())}
            placeholder="e.g. peanuts, shellfish"
          />
          <button type="button" onClick={addAllergy} className="btn-secondary w-24">
            Add
          </button>
        </div>
        {allergies.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {allergies.map((a) => (
              <span
                key={a}
                className="flex items-center gap-1 rounded-pill bg-forest-100 px-3 py-1 text-xs text-forest-700"
              >
                {a}
                <button
                  type="button"
                  onClick={() => setAllergies(allergies.filter((x) => x !== a))}
                  aria-label={`Remove ${a}`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-2 flex gap-3">
        <button className="btn-secondary" onClick={onBack} disabled={submitting}>
          Back
        </button>
        <button
          className="btn-primary"
          disabled={!canSubmit || submitting}
          onClick={() => onSubmit({ activityLevel, dietPattern, allergies })}
        >
          {submitting ? "Setting up..." : "Finish"}
        </button>
      </div>
    </div>
  );
}
